import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Upload, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail, BASE_URL } from "@/lib/api";

const TABS = ["products", "banners", "orders"];

const emptyProduct = { name: "", description: "", price: "", category: "", image: "", images: [], sizes: "S,M,L,XL", colors: "", size_guide: [], stock: 100, featured: false };
const emptyBanner = { title: "", subtitle: "", image: "", link: "", cta: "Shop Now", active: true };

export default function Admin() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [modal, setModal] = useState(null); // { type, data }

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/banners", { params: { active_only: false } }).then((r) => setBanners(r.data)).catch(() => {});
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const saveProduct = async (form) => {
    const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) || 0,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      colors: (form.colors || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (form.id) await api.put(`/products/${form.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Product saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveBanner = async (form) => {
    try {
      if (form.id) await api.put(`/banners/${form.id}`, form);
      else await api.post("/banners", form);
      toast.success("Banner saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/${kind}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-12 min-h-[70vh]">
      <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter uppercase mb-2">Admin</h1>
      <p className="text-muted-foreground text-sm mb-8 font-mono">Manage products, partner ad banners, and orders.</p>

      <div className="flex border-b border-border mb-8">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === t ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div>
          <button onClick={() => setModal({ type: "product", data: { ...emptyProduct } })} data-testid="add-product-button"
            className="mb-6 inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-black/80"><Plus size={14} /> Add Product</button>
          <div className="border border-border divide-y divide-border">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.id}`}>
                <img src={p.image} alt="" className="w-12 h-16 object-cover bg-secondary" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.category} · £{p.price.toFixed(2)} {p.featured && "· Featured"}</div>
                </div>
                <button onClick={() => setModal({ type: "product", data: { ...p, sizes: p.sizes.join(","), colors: (p.colors || []).join(",") } })} className="p-2 hover:text-accent" data-testid={`edit-product-${p.id}`}><Pencil size={16} /></button>
                <button onClick={() => del("products", p.id)} className="p-2 hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "banners" && (
        <div>
          <button onClick={() => setModal({ type: "banner", data: { ...emptyBanner } })} data-testid="add-banner-button"
            className="mb-6 inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-black/80"><Plus size={14} /> Add Banner</button>
          <div className="border border-border divide-y divide-border">
            {banners.map((b) => (
              <div key={b.id} className="flex items-center gap-4 p-4" data-testid={`admin-banner-${b.id}`}>
                <img src={b.image} alt="" className="w-16 h-12 object-cover bg-secondary" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{b.title}</div>
                  <div className="text-xs text-muted-foreground truncate">{b.subtitle} {b.active ? "· Active" : "· Hidden"}</div>
                </div>
                <button onClick={() => setModal({ type: "banner", data: { ...b } })} className="p-2 hover:text-accent" data-testid={`edit-banner-${b.id}`}><Pencil size={16} /></button>
                <button onClick={() => del("banners", b.id)} className="p-2 hover:text-destructive" data-testid={`delete-banner-${b.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="border border-border divide-y divide-border">
          {orders.length === 0 ? <div className="p-8 text-center text-muted-foreground text-sm">No orders yet.</div> :
            orders.map((o) => (
              <div key={o.id} className="p-4 flex justify-between items-center" data-testid={`admin-order-${o.id}`}>
                <div>
                  <div className="font-mono text-xs text-muted-foreground">{o.session_id?.slice(0, 20)}…</div>
                  <div className="text-sm mt-1">{o.email || "Guest"} · {o.items?.length} item(s)</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-medium">£{o.amount?.toFixed(2)}</div>
                  <div className="text-xs uppercase tracking-wider text-accent">{o.status}</div>
                </div>
              </div>
            ))}
        </div>
      )}

      {modal?.type === "product" && <ProductModal data={modal.data} onClose={() => setModal(null)} onSave={saveProduct} />}
      {modal?.type === "banner" && <BannerModal data={modal.data} onClose={() => setModal(null)} onSave={saveBanner} />}
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">{label}</label>
      <input {...props} className="w-full border border-input px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
    </div>
  );
}

function ImageUpload({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BASE_URL}${data.url}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mb-4">
      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">Image</label>
      {value && (
        <img src={value} alt="preview" className="w-full h-40 object-cover bg-secondary border border-border mb-2" data-testid="image-preview" />
      )}
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="image-file-input" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-image-button"
        className="w-full flex items-center justify-center gap-2 border border-foreground py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-secondary transition-colors disabled:opacity-60">
        {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> {value ? "Replace Image" : "Upload Image"}</>}
      </button>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL"
        className="w-full border border-input px-3 py-2 text-xs mt-2 outline-none focus:ring-2 focus:ring-ring" data-testid="image-url-input" />
    </div>
  );
}

function MultiImageUpload({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState("");
  const inputRef = useRef(null);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(`${BASE_URL}${data.url}`);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      }
    }
    if (uploaded.length) { onChange([...images, ...uploaded]); toast.success(`${uploaded.length} image(s) uploaded`); }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const addUrl = () => {
    if (url.trim()) { onChange([...images, url.trim()]); setUrl(""); }
  };
  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));

  return (
    <div className="mb-4">
      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">Images {images.length > 0 && `(${images.length})`}</label>
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-2" data-testid="image-gallery">
          {images.map((img, i) => (
            <div key={i} className="relative group">
              <img src={img} alt={`img-${i}`} className="w-full h-20 object-cover bg-secondary border border-border" data-testid={`gallery-image-${i}`} />
              {i === 0 && <span className="absolute top-0.5 left-0.5 bg-accent text-accent-foreground text-[8px] font-bold px-1 uppercase">Main</span>}
              <button type="button" onClick={() => removeAt(i)} data-testid={`remove-image-${i}`}
                className="absolute top-0.5 right-0.5 bg-black/70 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="image-file-input" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="upload-image-button"
        className="w-full flex items-center justify-center gap-2 border border-foreground py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-secondary transition-colors disabled:opacity-60">
        {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Upload Image(s)</>}
      </button>
      <div className="flex gap-2 mt-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste an image URL"
          className="flex-1 border border-input px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-ring" data-testid="image-url-input" />
        <button type="button" onClick={addUrl} className="border border-foreground px-3 text-xs font-bold uppercase hover:bg-secondary" data-testid="add-image-url-button">Add</button>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">First image is the main thumbnail. Hover an image to remove it.</p>
    </div>
  );
}


function SizeGuideEditor({ rows, onChange }) {
  const cols = ["chest", "length", "waist", "sleeve"];
  const addRow = () => onChange([...rows, { size: "", chest: "", length: "", waist: "", sleeve: "" }]);
  const setCell = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="mb-4" data-testid="size-guide-editor">
      <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">Size Guide (measurements in cm)</label>
      {rows.length > 0 && (
        <div className="border border-border">
          <div className="grid grid-cols-6 gap-1 bg-secondary px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider">
            <span>Size</span><span>Chest</span><span>Length</span><span>Waist</span><span>Sleeve</span><span></span>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-6 gap-1 px-2 py-1 items-center" data-testid={`size-guide-row-${i}`}>
              <input value={r.size} onChange={(e) => setCell(i, "size", e.target.value)} placeholder="M"
                className="border border-input px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-ring" data-testid={`size-guide-size-${i}`} />
              {cols.map((c) => (
                <input key={c} value={r[c]} onChange={(e) => setCell(i, c, e.target.value)} placeholder="—"
                  className="border border-input px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-ring" />
              ))}
              <button type="button" onClick={() => removeRow(i)} className="text-muted-foreground hover:text-destructive justify-self-center" data-testid={`remove-size-guide-${i}`}><X size={14} /></button>
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={addRow} data-testid="add-size-guide-row"
        className="mt-2 inline-flex items-center gap-1.5 border border-foreground px-3 py-2 text-xs font-bold uppercase tracking-[0.15em] hover:bg-secondary"><Plus size={12} /> Add size row</button>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ProductModal({ data, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Modal title={form.id ? "Edit Product" : "New Product"} onClose={onClose}>
      <Field label="Name" value={form.name} onChange={(e) => set("name", e.target.value)} />
      <div className="mb-4">
        <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">Description</label>
        <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3}
          className="w-full border border-input px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring" />
      </div>
      <Field label="Price (GBP £)" type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} />
      <Field label="Category" value={form.category} onChange={(e) => set("category", e.target.value)} />
      <MultiImageUpload images={form.images || []} onChange={(imgs) => set("images", imgs)} />
      <Field label="Sizes (comma separated)" value={form.sizes} onChange={(e) => set("sizes", e.target.value)} />
      <Field label="Colours (comma separated)" value={form.colors} onChange={(e) => set("colors", e.target.value)} placeholder="Black, White, Navy" />
      <SizeGuideEditor rows={form.size_guide || []} onChange={(r) => set("size_guide", r)} />
      <Field label="Stock" type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} />
      <label className="flex items-center gap-2 mb-6 text-sm font-medium">
        <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured on homepage
      </label>
      <button onClick={() => onSave(form)} data-testid="save-product-button"
        className="w-full bg-foreground text-background py-3.5 text-xs font-bold uppercase tracking-[0.25em] hover:bg-black/80">Save Product</button>
    </Modal>
  );
}

function BannerModal({ data, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Modal title={form.id ? "Edit Banner" : "New Banner"} onClose={onClose}>
      <Field label="Title" value={form.title} onChange={(e) => set("title", e.target.value)} />
      <Field label="Subtitle" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
      <ImageUpload value={form.image} onChange={(v) => set("image", v)} />
      <Field label="Link URL" value={form.link} onChange={(e) => set("link", e.target.value)} />
      <Field label="CTA Text" value={form.cta} onChange={(e) => set("cta", e.target.value)} />
      <label className="flex items-center gap-2 mb-6 text-sm font-medium">
        <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active (visible on site)
      </label>
      <button onClick={() => onSave(form)} data-testid="save-banner-button"
        className="w-full bg-foreground text-background py-3.5 text-xs font-bold uppercase tracking-[0.25em] hover:bg-black/80">Save Banner</button>
    </Modal>
  );
}
